import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Crown, Trophy, Zap } from "lucide-react";
import { http } from "@/lib/api";
import { useRealtimeTick } from "@/lib/realtime";
import { UserAvatar } from "@/components/UserAvatar";
import { MONTH_NAMES, fmtNum } from "@/lib/helpers";

export function useBadges() {
  const [data, setData] = useState({ current: null, champions: [], week_current: null, week_champions: [], mgr_current: null, mgr_champions: [], mgr_week_current: null, mgr_week_champions: [] });
  const tick = useRealtimeTick((m) => m.path.startsWith("/api/sales"));
  useEffect(() => { http.get("/badges").then((r) => setData(r.data)).catch(() => {}); }, [tick]);
  return data;
}

export function ChampionBadges({ userId, badges, size = "sm" }) {
  const wins = [...badges.champions, ...(badges.mgr_champions || [])].filter((c) => c.user_id === userId);
  const leading = badges.current?.user_id === userId || badges.mgr_current?.user_id === userId;
  const weekLead = badges.week_current?.user_id === userId || badges.mgr_week_current?.user_id === userId;
  const weekWins = [...(badges.week_champions || []), ...(badges.mgr_week_champions || [])].filter((c) => c.user_id === userId).length;
  if (!wins.length && !leading && !weekLead && !weekWins) return null;
  const cls = size === "sm" ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2 py-1";
  return (
    <span className="inline-flex flex-wrap items-center gap-1" data-testid={`badges-${userId}`}>
      {leading && (
        <span className={`inline-flex items-center gap-1 rounded-full border border-amber-400/60 bg-amber-400/15 text-amber-200 font-semibold ${cls}`}
              title="Bu ayın satış lideri" data-testid={`badge-current-${userId}`}><Crown size={11} /> Ayın Lideri</span>
      )}
      {weekLead && (
        <span className={`inline-flex items-center gap-1 rounded-full border border-cyan-400/60 bg-cyan-400/15 text-cyan-200 font-semibold ${cls}`}
              title="Bu haftanın satış lideri" data-testid={`badge-week-current-${userId}`}><Zap size={11} /> Haftanın Lideri</span>
      )}
      {weekWins > 0 && (
        <span className={`inline-flex items-center gap-1 rounded-full border border-cyan-500/40 bg-cyan-500/10 text-cyan-200 ${cls}`}
              title={`${weekWins} kez haftanın şampiyonu`} data-testid={`badge-week-wins-${userId}`}><Zap size={10} /> Hafta ×{weekWins}</span>
      )}
      {wins.map((w) => (
        <span key={`${w.year}-${w.month}`} className={`inline-flex items-center gap-1 rounded-full border border-yellow-500/40 bg-yellow-500/10 text-yellow-200 ${cls}`}
              title={`${MONTH_NAMES[w.month - 1]} ${w.year} şampiyonu · ${fmtNum(w.qty)} adet`} data-testid={`badge-${userId}-${w.year}-${w.month}`}>
          <Trophy size={10} /> {MONTH_NAMES[w.month - 1].slice(0, 3)} {String(w.year).slice(2)}
        </span>
      ))}
    </span>
  );
}

export const weekLabel = (w) => {
  const a = new Date(`${w.week_start}T00:00:00`), b = new Date(`${w.week_end}T00:00:00`);
  const f = (d) => `${d.getDate()} ${MONTH_NAMES[d.getMonth()].slice(0, 3)}`;
  return `${f(a)} – ${f(b)}`;
};

const THEMES = {
  month: { box: "border-yellow-400/50 from-yellow-500/20 via-orange-500/5", txt: "text-yellow-200", ic: "text-yellow-300", qty: "text-yellow-300", chip: "bg-yellow-400 shadow-yellow-500/40", Icon: Crown },
  week: { box: "border-cyan-400/50 from-cyan-500/20 via-blue-500/5", txt: "text-cyan-200", ic: "text-cyan-300", qty: "text-cyan-300", chip: "bg-cyan-400 shadow-cyan-500/40", Icon: Zap },
};

export function WeekLeaderCard({ leader, tv }) {
  if (!leader) return null;
  return <LeaderCard leader={leader} tv={tv} kind="week" label="Haftanın Lideri" testId="week-leader" />;
}

export function MonthLeaderCard({ leader, tv }) {
  if (!leader) return null;
  return <LeaderCard leader={leader} tv={tv} kind="month" label={`Ayın Lideri · ${MONTH_NAMES[leader.month - 1]}`} testId="month-leader" />;
}

export function MgrMonthLeaderCard({ leader, tv }) {
  if (!leader) return null;
  return <LeaderCard leader={leader} tv={tv} kind="month" label={`Ayın Müdürü · ${MONTH_NAMES[leader.month - 1]}`} testId="mgr-month-leader" sub="şube toplamı" />;
}

export function MgrWeekLeaderCard({ leader, tv }) {
  if (!leader) return null;
  return <LeaderCard leader={leader} tv={tv} kind="week" label="Haftanın Müdürü" testId="mgr-week-leader" sub="şube toplamı" />;
}

function LeaderCard({ leader, tv, kind, label, testId, sub }) {
  const t = THEMES[kind];
  return (
    <motion.div layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className={`relative m-2 mb-0 rounded-xl border bg-gradient-to-br to-transparent p-3 overflow-hidden ${t.box}`} data-testid={`${testId}-card`}>
      <div className={`flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] mb-2 ${t.txt}`}>
        <t.Icon size={12} className={t.ic} /> {label}
      </div>
      <div className="flex items-center gap-3">
        <div className="relative">
          <UserAvatar path={leader.photo_path} name={leader.name} className={tv ? "h-20 w-20" : "h-16 w-16"} rounded="rounded-2xl" />
          <span className={`absolute -top-2 -right-2 h-7 w-7 rounded-full flex items-center justify-center shadow-lg ${t.chip}`}>
            <t.Icon size={14} className="text-slate-900" />
          </span>
        </div>
        <div className="min-w-0">
          <div className="font-display font-bold text-base leading-tight truncate" data-testid={`${testId}-name`}>{leader.name}</div>
          <div className="text-[11px] text-slate-400 truncate">{leader.branch_name || "-"}</div>
          <div className={`font-display text-2xl font-black leading-tight ${t.qty}`} data-testid={`${testId}-qty`}>{fmtNum(leader.qty)} <span className="text-xs font-semibold">adet{sub ? ` · ${sub}` : ""}</span></div>
        </div>
      </div>
    </motion.div>
  );
}
